import { useState, type ReactElement } from 'react'
import { toast } from 'sonner'
import { ActionPopover } from '@/components/action-popover'
import { ComposeTextarea } from '@/components/compose-field'
import { ReviewChoiceGroup, type ReviewChoiceOption } from '@/components/review-choice-group'
import { Input } from '@/components/ui/input'
import { getApiErrorMessage } from '@/lib/api-error'
import { formatMoney } from '@/lib/utils'
import {
  CATEGORY_KEYS,
  CATEGORY_LABELS,
  type Coverage,
  type ExemptCategory,
  dateFieldValue,
  formatDay,
  useApproveTaxExemption,
  estimatedTaxTotal,
  useDeclineTaxExemption,
  useUpdateTaxExemption,
  type TaxExemptionDetail,
} from './api'

// The sheets that decide a form (USA-1504). They open from the form's action item card (Approve
// form, Reject form) and from its status pill (reject an approved form, approve a declined one);
// the page itself has no decision buttons of its own.

const STAFF_NOTE = { label: 'Note for staff', placeholder: 'Note for staff (optional)', maxLength: 2000 }

function failed(error: unknown) {
  toast.error(getApiErrorMessage(error, 'That did not save. Try again.'))
}

/** How a sheet opens: from its own trigger, or (`anchorOnly`, `open`) from a menu beside it. */
type Opening = {
  trigger: ReactElement
  anchorOnly?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function CustomerMessage({ value, onChange, disabled }: { value: string; onChange: (value: string) => void; disabled: boolean }) {
  return (
    <label className="grid gap-1 text-xs font-medium text-muted-foreground">
      Message to the customer
      <ComposeTextarea
        className="min-h-20 text-sm font-normal text-foreground"
        disabled={disabled}
        maxLength={1000}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Why we can't accept this form"
        required
        value={value}
      />
    </label>
  )
}

const COVERAGE_OPTIONS = [
  { value: 'all', label: 'All items' },
  { value: 'some', label: 'Only some items' },
] as const satisfies ReadonlyArray<ReviewChoiceOption<Coverage>>

/**
 * Approve a form (what it covers, until when), or with `edit` change what an approved form covers:
 * either way the customer is emailed.
 */
export function ApproveForm({
  form,
  edit = false,
  only,
  trigger,
  anchorOnly,
  open,
  onOpenChange,
}: {
  form: TaxExemptionDetail
  edit?: boolean
  /** Just what it covers (its expiry edits on its own). */
  only?: 'coverage'
} & Opening) {
  const approve = useApproveTaxExemption(form.uuid)
  const update = useUpdateTaxExemption(form.uuid)
  const pending = approve.isPending || update.isPending
  const initial = dateFieldValue(form.expires_on ?? (edit ? null : form.ai_review?.expires_on))
  const initialCoverage: Coverage = form.coverage ?? form.ai_review?.coverage ?? 'all'
  const initialCategories =
    form.exempt_categories.length > 0 ? form.exempt_categories : (form.ai_review?.covered_categories ?? [])
  const [expires, setExpires] = useState(initial)
  const [coverage, setCoverage] = useState<Coverage>(initialCoverage)
  const [categories, setCategories] = useState<ExemptCategory[]>(initialCategories)
  const toggle = (key: ExemptCategory, on: boolean) =>
    setCategories((current) => (on ? [...current, key] : current.filter((item) => item !== key)))
  return (
    <ActionPopover
      anchorOnly={anchorOnly}
      canConfirm={coverage === 'all' || categories.length > 0}
      confirmLabel={edit ? 'Save and email the customer' : 'Approve'}
      confirmVariant="default"
      note={STAFF_NOTE}
      onConfirm={(note, close) =>
        edit
          ? update.mutate(
              { note, expires_on: expires || null, coverage, exempt_categories: categories },
              { onSuccess: close, onError: failed },
            )
          : approve.mutate(
              { note, expires_on: expires || undefined, coverage, exempt_categories: categories },
              { onSuccess: close, onError: failed },
            )
      }
      onOpenChange={(next) => {
        if (next) {
          setExpires(initial)
          setCoverage(initialCoverage)
          setCategories(initialCategories)
        }
        onOpenChange?.(next)
      }}
      open={open}
      pending={pending}
      pendingLabel={edit ? 'Saving...' : 'Approving...'}
      title={edit ? 'What it covers' : 'Approve this form'}
      trigger={trigger}
    >
      <div className="grid gap-1 text-xs font-medium text-muted-foreground">
        Tax-free on
        <ReviewChoiceGroup
          disabled={pending}
          label="Tax-free on"
          onChange={setCoverage}
          options={COVERAGE_OPTIONS}
          value={coverage}
        />
      </div>
      {coverage === 'some' ? (
        <fieldset className="grid max-h-56 gap-1.5 overflow-y-auto text-sm" aria-label="Tax-free categories">
          {CATEGORY_KEYS.map((key) => (
            <label className="flex cursor-pointer items-center gap-2" key={key}>
              <input
                checked={categories.includes(key)}
                className="size-4 shrink-0 cursor-pointer accent-primary"
                disabled={pending}
                onChange={(event) => toggle(key, event.target.checked)}
                type="checkbox"
              />
              {CATEGORY_LABELS[key]}
            </label>
          ))}
        </fieldset>
      ) : null}
      {only === 'coverage' ? null : (
        <div className="grid gap-1 text-xs text-muted-foreground">
          <label className="grid gap-1 font-medium">
            Expires (optional)
            <Input
              className="h-9 text-sm font-normal text-foreground"
              disabled={pending}
              onChange={(event) => setExpires(event.target.value)}
              type="date"
              value={expires}
            />
          </label>
          <span>{initial ? 'Filled in from the form.' : "The form shows no end date. Leave blank if it doesn't expire."}</span>
        </div>
      )}
    </ActionPopover>
  )
}

/**
 * Reject a form with the message the customer is emailed. For an approved form this also removes
 * the exemption, and can bill the tax on orders placed tax-free under it.
 */
export function RejectForm({ form, trigger, anchorOnly, open, onOpenChange }: { form: TaxExemptionDetail } & Opening) {
  const decline = useDeclineTaxExemption(form.uuid)
  const approved = form.status === 'approved'
  const initial = approved ? '' : (form.ai_review?.customer_message ?? '')
  const [message, setMessage] = useState(initial)
  const [billTax, setBillTax] = useState(true)
  const orders = approved ? form.tax_free_orders : []
  const total = estimatedTaxTotal(orders)
  return (
    <ActionPopover
      anchorOnly={anchorOnly}
      canConfirm={Boolean(message.trim())}
      confirmLabel="Reject and email the customer"
      confirmVariant={approved ? 'destructive' : 'default'}
      note={STAFF_NOTE}
      onConfirm={(note, close) =>
        decline.mutate(
          { customer_message: message.trim(), note, bill_tax: orders.length > 0 && billTax },
          { onSuccess: close, onError: failed },
        )
      }
      onOpenChange={(next) => {
        if (next) {
          setMessage(initial)
          setBillTax(true)
        }
        onOpenChange?.(next)
      }}
      open={open}
      pending={decline.isPending}
      pendingLabel="Rejecting..."
      title={approved ? 'Reject and remove the exemption' : 'Reject this form'}
      trigger={trigger}
    >
      <CustomerMessage disabled={decline.isPending} onChange={setMessage} value={message} />
      {orders.length ? (
        <div className="order-last grid gap-2">
          <label className="flex cursor-pointer items-start gap-2 text-sm">
            <input
              checked={billTax}
              className="mt-0.5 size-4 shrink-0 cursor-pointer accent-primary"
              disabled={decline.isPending}
              onChange={(event) => setBillTax(event.target.checked)}
              type="checkbox"
            />
            <span>
              Bill the tax on orders placed tax-free
              <span className="block text-xs tabular-nums text-muted-foreground">
                {formatMoney(total)} on {orders.length} {orders.length === 1 ? 'order' : 'orders'}
              </span>
            </span>
          </label>
          {billTax ? (
            <ul aria-label="Orders to bill" className="max-h-40 divide-y overflow-y-auto border-y text-xs">
              {orders.map((order) => (
                <li className="flex items-baseline justify-between gap-3 py-1.5" key={order.shopify_order_uuid}>
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{order.name}</span>
                    <span className="text-muted-foreground"> · {formatDay(order.created_at)}</span>
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {order.estimated_tax != null ? formatMoney(order.estimated_tax) : <span className="text-muted-foreground">No rate</span>}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </ActionPopover>
  )
}
